import React, { useEffect } from 'react';

import Icon, { faBusinessTime, faCircleCheck, faCircleXmark, faFileInvoice } from '~components/icon';
import Spinner from '~components/Spinner';

import { useGetClaimsEstimateQuery } from '~support-at-home/actions/claims';

import { SummaryStatCard } from '../common';
import ClaimRange from './ClaimRange';

interface ComponentProps {
  value: { start_date: string; end_date: string; naps_id?: string[]; status_id?: string[]; service_type_id?: string[] };
  onChange?: (name: string, value: any) => void;
}

const Summary: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { start_date, end_date, naps_id, status_id, service_type_id } = value;

  const { data, isLoading } = useGetClaimsEstimateQuery(
    { start_date, end_date, naps_id, status_id, service_type_id },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Expose data to parent form for validation
  useEffect(() => {
    if (onChange && data) {
      onChange('summary', { data });
    }
  }, [data]);

  const { appointmentsCount = 0, supplierInvoiceItemsCount = 0, total = 0 } = data || {};

  const renderCount = (count: number) =>
    isLoading ? <Spinner loading className="spinner-container" /> : <span>{count}</span>;

  return (
    <div>
      <ClaimRange value={value} showNapsFilter />
      <div className="row mb-3">
        <div className="d-flex flex-wrap gap-4">
          <div className="flex-fill">
            <SummaryStatCard
              count={renderCount(appointmentsCount)}
              countLabel="Total Appointments"
              title="Appointments"
              icon={<Icon icon={faBusinessTime} />}
              criteria={[
                'Service date is within nominated claiming period',
                'Appointment is not included in another claim',
                'Appointment package is a part of a Support at Home Funding Type',
              ]}
            />
          </div>
          <div className="flex-fill">
            <SummaryStatCard
              count={renderCount(supplierInvoiceItemsCount)}
              countLabel="Total Invoice Lines"
              title="3rd Party Invoice Lines"
              icon={<Icon icon={faFileInvoice} />}
              criteria={[
                'Item service date within claiming period',
                'Item is not included in another claim',
                'Invoice status is either Approved or Approved - Paid',
              ]}
            />
          </div>
        </div>
      </div>
      {!isLoading && (
        <div className="d-flex align-items-center justify-content-between bg-light border rounded p-3 mb-3">
          {total === 0 ? (
            <div className="text-danger">No claimable items found</div>
          ) : (
            <div className="d-flex flex-column">
              <div className="text-muted mb-1">Total Items to Claim</div>
              <div className="fs-4 fw-bold">{renderCount(total)}</div>
            </div>
          )}
          <div className="d-flex flex-column">
            {total === 0 ? (
              <div className="text-danger">
                <Icon icon={faCircleXmark} size="xl" />
              </div>
            ) : (
              <div className="text-muted">
                <Icon icon={faCircleCheck} size="xl" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Summary;
